/**
 * aaa-cwd-media-gallery  (re-synced 2026-06-09)
 * ---------------------------------------------------------------------------
 * Saddle media gallery that swaps its full image set (4 angles) when the
 * customer changes the Finish variant or picks a customisation. Replaces the
 * stock _product-media-gallery for products that carry the custom.cwd_customiser
 * metafield (the product-information section renders this block instead for
 * those products; everything else keeps the stock gallery).
 *
 * Sources:
 *   - images keyed "custId|finId" -> [url1,url2,url3,url4] in the config metafield,
 *   - Finish from the native variant ('variant:update'),
 *   - Customisation from the aaa-cwd-customiser block ('cwd:customisation').
 *
 * Colour is fixed per page (each colour is its own combined-listing child), so
 * switching colour navigates to the other child and re-renders this gallery
 * server-side; we only react to finish + customisation here.
 *
 * Dependency-free island (F.9). 'variant:update' === ThemeEvents.variantUpdate.
 */
(function () {
  var VARIANT_UPDATE = 'variant:update';
  var CUSTOMISATION = 'cwd:customisation';

  if (customElements.get('aaa-cwd-media-gallery')) return;

  function lc(v) {
    return String(v == null ? '' : v).trim().toLowerCase();
  }
  function withWidth(url, w) {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  // Stored image values are filename suffixes joined to config.imageBase; a value
  // that is already an absolute URL is used as-is (defensive across config formats).
  function resolve(base, v) {
    if (!v) return v;
    return v.indexOf('http') === 0 ? v : base + v;
  }

  class CwdMediaGallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.config = this._readConfig();
      // Prefer the server-rendered base (data-image-base) — Liquid drops the camelCase
      // imageBase key from the parsed metafield, so config.imageBase is unreliable.
      this.base =
        this.getAttribute('data-image-base') || (this.config && this.config.imageBase) || '';
      this.mainImg = this.querySelector('[data-cwd-gallery-main]');
      this.thumbs = this.querySelector('[data-cwd-thumbs]');
      this.prevBtn = this.querySelector('[data-cwd-prev]');
      this.nextBtn = this.querySelector('[data-cwd-next]');
      if (!this.config || !this.config.images || !this.mainImg) return;

      this.finByName = {};
      this.finById = {};
      (this.config.finishes || []).forEach((f) => {
        this.finByName[lc(f.name)] = String(f.id);
        this.finById[String(f.id)] = f.name;
      });
      this.custById = {};
      (this.config.customisations || []).forEach((c) => {
        this.custById[String(c.id)] = c.name;
      });
      this.productTitle = this.getAttribute('data-product-title') || '';

      this.finishPos = parseInt(this.getAttribute('data-finish-option-position') || '0', 10);
      this.currentFin =
        this.finByName[lc(this.getAttribute('data-initial-finish'))] ||
        (this.config.finishes && this.config.finishes[0] && String(this.config.finishes[0].id)) ||
        '44';
      this.currentCust = this.getAttribute('data-default-cust') || this._firstCust();
      this.index = 0;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._step(1));

      // Finish (native variant) changes
      this._onVariant = (e) => {
        var v = e && e.detail && e.detail.resource;
        if (!v) return;
        var finName = this.finishPos
          ? v['option' + this.finishPos] || (v.options && v.options[this.finishPos - 1])
          : null;
        if (!finName) return;
        var id = this.finByName[lc(finName)];
        if (id && id !== this.currentFin) {
          this.currentFin = id;
          this._render(true); // keep angle index
        }
      };
      document.addEventListener(VARIANT_UPDATE, this._onVariant);

      // Customisation (piping) changes from the customiser block
      this._onCust = (e) => {
        var id = e && e.detail && e.detail.custId;
        if (id == null) return;
        if (String(id) !== this.currentCust) {
          this.currentCust = String(id);
          this.index = 0; // jump to the cover angle that shows the piping
          this._render(false);
        }
      };
      document.addEventListener(CUSTOMISATION, this._onCust);

      this._render(false);
    }

    disconnectedCallback() {
      if (this._onVariant) document.removeEventListener(VARIANT_UPDATE, this._onVariant);
      if (this._onCust) document.removeEventListener(CUSTOMISATION, this._onCust);
    }

    _readConfig() {
      try {
        var s = this.querySelector('[data-cwd-config]');
        return s ? JSON.parse(s.textContent) : null;
      } catch (e) {
        return null;
      }
    }
    _firstCust() {
      var c = this.config.customisations && this.config.customisations[0];
      return c ? String(c.id) : null;
    }

    _imagesFor(cust, fin) {
      var imgs = this.config.images || {};
      var arr = imgs[cust + '|' + fin] || imgs[cust + '|44'];
      if (!arr) {
        var keys = Object.keys(imgs);
        for (var i = 0; i < keys.length; i++) {
          if (keys[i].split('|')[0] === String(cust)) {
            arr = imgs[keys[i]];
            break;
          }
        }
      }
      // tolerate either a string (legacy single image) or an array
      if (typeof arr === 'string') arr = [arr];
      return arr && arr.length ? arr : null;
    }

    _step(dir) {
      var imgs = this._imagesFor(this.currentCust, this.currentFin);
      if (!imgs) return;
      this.index = (this.index + dir + imgs.length) % imgs.length;
      this._paintMain(imgs);
      this._markActiveThumb();
    }

    _paintMain(imgs) {
      var v = imgs[Math.min(this.index, imgs.length - 1)];
      if (v) this.mainImg.setAttribute('src', withWidth(resolve(this.base, v), 800));
      this._setAlt();
    }

    // Keep the main image alt descriptive (title + piping colour + finish) as the
    // customer changes selection, so it's meaningful for SEO and screen readers.
    _setAlt() {
      if (!this.mainImg || !this.productTitle) return;
      var alt = this.productTitle;
      var cust = this.custById[this.currentCust];
      var fin = this.finById[this.currentFin];
      if (cust) alt += ' in ' + cust + ' piping';
      if (fin) alt += ', ' + fin;
      this.mainImg.setAttribute('alt', alt);
    }

    // Descriptive alt for a gallery image: title + piping + finish + angle position.
    // Thumbs sit inside buttons that carry their own aria-label, so the alt is
    // primarily for image SEO; screen readers announce the button label.
    _altFor(position, total) {
      var alt = this.productTitle || 'Saddle';
      var cust = this.custById[this.currentCust];
      var fin = this.finById[this.currentFin];
      if (cust) alt += ' in ' + cust + ' piping';
      if (fin) alt += ', ' + fin;
      if (total > 1) alt += ' - view ' + position + ' of ' + total;
      return alt;
    }

    _markActiveThumb() {
      if (!this.thumbs) return;
      Array.prototype.forEach.call(this.thumbs.querySelectorAll('[data-cwd-thumb]'), (b) => {
        var on = parseInt(b.getAttribute('data-cwd-thumb'), 10) === this.index;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }

    _render(keepIndex) {
      var imgs = this._imagesFor(this.currentCust, this.currentFin);
      if (!imgs) return;
      if (!keepIndex || this.index >= imgs.length) this.index = 0;

      this._paintMain(imgs);

      if (this.thumbs) {
        this.thumbs.innerHTML = '';
        imgs.forEach((url, i) => {
          var li = document.createElement('li');
          li.className = 'cwd-gallery__thumb';
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.setAttribute('data-cwd-thumb', String(i));
          btn.setAttribute('aria-label', 'View image ' + (i + 1));
          var im = document.createElement('img');
          im.src = withWidth(resolve(this.base, url), 200);
          im.alt = this._altFor(i + 1, imgs.length);
          im.loading = 'lazy';
          im.decoding = 'async';
          btn.appendChild(im);
          btn.addEventListener('click', () => {
            this.index = i;
            this._paintMain(imgs);
            this._markActiveThumb();
          });
          li.appendChild(btn);
          this.thumbs.appendChild(li);
        });
        this._markActiveThumb();
      }
    }
  }

  customElements.define('aaa-cwd-media-gallery', CwdMediaGallery);
})();
